import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { Mascot } from "@/pages/student/Mascot";

/*
 * "Ask Butler" on the guided demo. Scripted, not AI: a question is matched to
 * a prepared answer by keywords, and the answer names a spot on the page for
 * the demo to highlight.
 */

export type ButlerSpot = "grade" | "dm" | "profile" | "results" | "points" | "terms" | "preview" | "try";

type Answer = { spot: ButlerSpot; label: string; keys: string[]; text: string };

const ANSWERS: Answer[] = [
  {
    spot: "grade",
    label: "How do I grade a discussion?",
    keys: ["grade", "grading", "score", "mark"],
    text: "Open the discussion in the lesson, read a reply, type the grade (like 7 out of 10), add a comment if you like, then click Save mark. I've highlighted the first reply that still needs a grade.",
  },
  {
    spot: "profile",
    label: "How do I contact a parent?",
    keys: ["parent", "guardian", "mom", "dad", "family", "profile"],
    text: "Click a student's name to open their profile. You'll see how they're doing, their parents' email and phone, and a Message button for each parent. Try Ava's, I've highlighted it.",
  },
  {
    spot: "dm",
    label: "How do I message a student?",
    keys: ["message", "dm", "contact", "talk to", "email"],
    text: "Click a student's picture next to their reply. Try Maya's, I've highlighted it.",
  },
  {
    spot: "results",
    label: "How did the class do?",
    keys: ["class do", "results", "missed", "analytics", "how did", "average", "turned in", "submission"],
    text: "Open an item and click Results. You'll see who turned it in, the class average, which question most students missed and what they answered instead, and every student. I've opened the Lesson 1.1 check for you.",
  },
  {
    spot: "points",
    label: "How do points and attempts work?",
    keys: ["attempt", "tries", "worth", "points"],
    text: "Each item has its own points and number of tries. This check is worth 40 points, gives students 3 tries, and grades itself.",
  },
  {
    spot: "terms",
    label: "How do I add key terms?",
    keys: ["key term", "terms", "flashcard", "vocab"],
    text: "Each lesson has a Key terms item. Add a word and its definition, and students get it as a flashcard. Here are the six in Lesson 1.1.",
  },
  {
    spot: "preview",
    label: "What do students see?",
    keys: ["student see", "students see", "preview", "as a student", "student view", "students view"],
    text: "Open any item and click Student at the top of its panel to see it the way students do. Preview course walks through the whole course.",
  },
  {
    spot: "try",
    label: "How do I make my own course?",
    keys: ["create", "my own", "new course", "build", "try it"],
    text: "Click Try it yourself. Name your course, then start from our template with the lessons laid out, or start blank.",
  },
];

type Line = { from: "butler" | "you"; text: string };

function match(question: string): Answer | null {
  const text = question.toLowerCase();
  return ANSWERS.find((answer) => answer.keys.some((key) => text.includes(key))) || null;
}

export default function ButlerChat({
  onSpot,
  onOpenChange,
}: {
  onSpot: (spot: ButlerSpot) => void;
  onOpenChange?: (open: boolean) => void;
}) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    onOpenChange?.(open);
  }, [open, onOpenChange]);

  // Closing the chat (or leaving the page) gives the room back.
  useEffect(() => () => onOpenChange?.(false), [onOpenChange]);
  const [draft, setDraft] = useState("");
  const [lines, setLines] = useState<Line[]>([
    { from: "butler", text: "Hi! Ask me how to do something, like grading a discussion, and I'll point you to it." },
  ]);
  const logRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = logRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [lines, open]);

  const ask = (question: string, chosen?: Answer) => {
    const text = question.trim();
    if (!text) return;
    // A suggested question carries its own answer; a typed one is matched by keywords.
    const answer = chosen || match(text);
    setLines((all) => [
      ...all,
      { from: "you", text },
      {
        from: "butler",
        text: answer ? answer.text : "I'm not sure about that one yet. Try one of the questions below.",
      },
    ]);
    setDraft("");
    if (answer) onSpot(answer.spot);
  };

  return (
    <div className="al-demo-chat-wrap">
      {open ? (
        <section className="al-demo-chat" aria-label="Ask Butler">
          <div className="al-demo-chat-head">
            <Mascot pose="laptop" size={36} />
            <div>
              <p className="al-demo-chat-title">Ask Butler</p>
              <p className="al-demo-chat-sub">I'll point you to the right spot</p>
            </div>
            <button type="button" className="al-demo-chat-close" onClick={() => setOpen(false)} aria-label="Close chat">
              <X size={16} aria-hidden="true" />
            </button>
          </div>
          <div className="al-demo-chat-log" ref={logRef} aria-live="polite">
            {lines.map((line, index) => (
              <p key={index} className={`al-demo-chat-line al-demo-chat-line--${line.from}`}>
                {line.text}
              </p>
            ))}
            <div className="al-demo-chat-suggest">
              {ANSWERS.map((answer) => (
                <button key={answer.spot} type="button" onClick={() => ask(answer.label, answer)}>
                  {answer.label}
                </button>
              ))}
            </div>
          </div>
          <form
            className="al-demo-chat-form"
            onSubmit={(event) => {
              event.preventDefault();
              ask(draft);
            }}
          >
            <label className="al-demo-sr" htmlFor="al-demo-chat-input">
              Ask Butler a question
            </label>
            <input
              id="al-demo-chat-input"
              type="text"
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              placeholder="How do I grade a discussion?"
              autoFocus
            />
            <button type="submit" className="al-demo-btn">
              Ask
            </button>
          </form>
        </section>
      ) : null}
      <button type="button" className="al-demo-chat-toggle" onClick={() => setOpen((on) => !on)} aria-expanded={open}>
        <Mascot pose="waving" size={40} />
        Ask Butler
      </button>
    </div>
  );
}
